import React, { createContext, useState, useContext, useCallback } from "react";

const MessageSearchContext = createContext();

/**
 * O alvo do Ctrl+K é este ticket? A rota usa UUID (`useParams`) e o resultado
 * da busca traz `ticket.uuid`. Compara só por UUID — o fallback numérico
 * casava o ticket ainda defasado na troca manual e vazava o termo para o próximo.
 */
export const isHighlightTargetTicket = ({ highlightTicketUuid, routeTicketId }) =>
  Boolean(highlightTicketUuid && routeTicketId && String(highlightTicketUuid) === String(routeTicketId));

export const MessageSearchProvider = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  const [highlightMessageId, setHighlightMessageId] = useState(null);
  const [highlightTicketId, setHighlightTicketId] = useState(null);
  const [highlightTicketUuid, setHighlightTicketUuid] = useState(null);

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setSearchResults([]);
    setCurrentResultIndex(-1);
    setHighlightMessageId(null);
    setHighlightTicketId(null);
    setHighlightTicketUuid(null);
  }, []);

  /** Alvo da navegação vinda do Ctrl+K: mensagem X dentro do ticket Y. */
  const setHighlightTarget = useCallback(({ ticketId, ticketUuid, messageId, term }) => {
    setHighlightTicketId(ticketId ?? null);
    setHighlightTicketUuid(ticketUuid ?? null);
    setHighlightMessageId(messageId ?? null);
    if (typeof term === "string") setSearchTerm(term);
  }, []);

  const goToNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    setHighlightMessageId(searchResults[nextIndex]);
  }, [searchResults, currentResultIndex]);

  const goToPreviousResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIndex =
      currentResultIndex - 1 < 0
        ? searchResults.length - 1
        : currentResultIndex - 1;
    setCurrentResultIndex(prevIndex);
    setHighlightMessageId(searchResults[prevIndex]);
  }, [searchResults, currentResultIndex]);

  return (
    <MessageSearchContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        searchResults,
        setSearchResults,
        currentResultIndex,
        setCurrentResultIndex,
        highlightMessageId,
        setHighlightMessageId,
        highlightTicketId,
        highlightTicketUuid,
        setHighlightTarget,
        clearSearch,
        goToNextResult,
        goToPreviousResult,
      }}
    >
      {children}
    </MessageSearchContext.Provider>
  );
};

export const useMessageSearch = () => {
  const context = useContext(MessageSearchContext);
  if (!context) {
    throw new Error(
      "useMessageSearch must be used within a MessageSearchProvider"
    );
  }
  return context;
};

